import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Users, BarChart3, Target, TrendingUp, Award, Loader2,
  Building2, Search, ChevronDown, ChevronUp, MessageSquare,
  Briefcase, Handshake, LogOut, RefreshCw, ExternalLink, Copy
} from "lucide-react";

const SponsorDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedMember, setExpandedMember] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/sponsor/dashboard`);
      if (res.ok) setData(await res.json());
      else toast.error("Failed to load dashboard");
    } catch (e) { toast.error("Error loading dashboard"); }
    setLoading(false);
  };

  useEffect(() => { fetchDashboard(); }, []);

  const portalUrl = data ? `${window.location.origin}/partner/${data.organization?.slug}` : "";

  const filteredMembers = (data?.members || []).filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-white">
      <div className="text-center">
        <h1 className="text-xl font-bold mb-2">Dashboard Unavailable</h1>
        <p className="text-neutral-400 mb-4">Your sponsor account may be pending approval.</p>
        <Button onClick={() => navigate("/")} variant="outline" className="border-neutral-700 text-white">Go Home</Button>
      </div>
    </div>
  );

  const org = data.organization;
  const stats = data.stats;

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Header */}
      <header className="border-b border-neutral-800 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {org.logo_url && <img src={org.logo_url} alt={org.name} className="h-10 w-auto" />}
            <div>
              <h1 className="text-lg font-bold text-white">{org.name}</h1>
              <p className="text-xs text-[#39FF14] uppercase tracking-wider">Sponsor Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-400 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/login"); }} className="text-neutral-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Portal URL */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-neutral-400 mb-1">Your Co-Branded Portal URL</p>
                <p className="text-white font-mono text-sm bg-neutral-800 px-3 py-2 rounded-lg">{portalUrl}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("URL copied!"); }} className="bg-neutral-800 hover:bg-neutral-700 text-white">
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button size="sm" onClick={() => window.open(portalUrl, "_blank")} variant="outline" className="border-neutral-700 text-white">
                  <ExternalLink className="w-4 h-4 mr-1" /> Preview
                </Button>
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-3">Share this link with your entrepreneurs to register for the program.</p>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Total Members", value: stats.total_members, icon: Users, color: "#39FF14" },
            { label: "Active", value: stats.active_members, icon: Target, color: "#3b82f6" },
            { label: "Onboarded", value: stats.onboarded, icon: Award, color: "#f59e0b" },
            { label: "Avg Points", value: stats.avg_points, icon: TrendingUp, color: "#a855f7" },
            { label: "Connections", value: stats.total_connections, icon: Handshake, color: "#ec4899" },
            { label: "Posts", value: stats.total_discussion_posts, icon: MessageSquare, color: "#06b6d4" },
            { label: "Opportunities", value: stats.total_opportunities, icon: Briefcase, color: "#f97316" }
          ].map((s, i) => (
            <Card key={i} className="bg-neutral-900 border-neutral-800">
              <CardContent className="pt-4 pb-3 text-center">
                <s.icon className="w-5 h-5 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-[10px] text-neutral-500 uppercase tracking-wide">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Members List */}
        <Card className="bg-neutral-900 border-neutral-800">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-white">Cohort Members</CardTitle>
                <CardDescription className="text-neutral-400">{stats.total_members} entrepreneurs in your program</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members..." className="pl-9 bg-neutral-800 border-neutral-700 text-white w-48" />
                </div>
                <Button size="sm" variant="ghost" onClick={fetchDashboard} className="text-neutral-400">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredMembers.length > 0 ? (
              <div className="space-y-2">
                {filteredMembers.map((member) => {
                  const isExpanded = expandedMember === member.user_id;
                  return (
                    <div key={member.user_id} className="bg-neutral-800 rounded-lg border border-neutral-700 overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-750"
                        onClick={() => setExpandedMember(isExpanded ? null : member.user_id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center overflow-hidden">
                            {member.picture ? <img src={member.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <Users className="w-5 h-5 text-[#39FF14]" />}
                          </div>
                          <div>
                            <p className="font-medium text-white">{member.name}</p>
                            <p className="text-xs text-neutral-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right hidden sm:block">
                            <p className="text-sm font-medium text-[#39FF14]">{member.total_points} pts</p>
                            <p className="text-xs text-neutral-500">{member.onboarding_complete ? "Onboarded" : "In Progress"}</p>
                          </div>
                          {member.onboarding_complete ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs hidden sm:flex">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-xs hidden sm:flex">Setup</Badge>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-neutral-700 pt-3">
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {[
                              { label: "Points", value: member.total_points },
                              { label: "Connections", value: member.connections },
                              { label: "Pods Joined", value: member.pods_joined },
                              { label: "Posts", value: member.discussion_posts },
                              { label: "Opportunities", value: member.opportunities_posted }
                            ].map((stat, i) => (
                              <div key={i} className="bg-neutral-900 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-white">{stat.value}</p>
                                <p className="text-[10px] text-neutral-500 uppercase">{stat.label}</p>
                              </div>
                            ))}
                          </div>
                          {member.business_description && (
                            <p className="text-sm text-neutral-400 mt-3 bg-neutral-900 p-3 rounded-lg">{member.business_description}</p>
                          )}
                          <p className="text-xs text-neutral-600 mt-2">Joined: {member.created_at ? new Date(member.created_at).toLocaleDateString() : "N/A"}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="mb-2">{search ? "No members match your search" : "No members have registered yet"}</p>
                <p className="text-xs">Share your portal URL to start enrolling entrepreneurs</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SponsorDashboard;
